import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link, useParams} from "react-router-dom";
import {LessonDetailsResponse} from "../types";
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Container,
    Paper,
    Typography
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {getAdminLessonDetailsResponse} from "../api/api";
import {useAlert} from "../context/AlertContext";
import CodeIcon from "@mui/icons-material/Code";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BreadcrumbLink from "../components/BreadcrumbLink";

const LessonDetailsPage = () => {
    const {t} = useTranslation();

    const {lessonId} = useParams<{ lessonId?: string }>();
    const safeLessonId = lessonId ?? "";

    const [lesson, setLesson] = useState<LessonDetailsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const {showAlert} = useAlert();

    const load = async (opts?: { silent?: boolean }) => {
        if (!safeLessonId) return;

        const silent = Boolean(opts?.silent);

        if (!silent) setLoading(true);
        setHasError(false);

        try {
            const id = Number(safeLessonId);
            const res = await getAdminLessonDetailsResponse(id);
            setLesson(res);
        } catch (error: any) {
            setHasError(true);
            setLesson(null);
            showAlert(
                error?.message ?? t("lessondetails.errors.loadFailed"),
                "error"
            );
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        if (!safeLessonId) return;

        let cancelled = false;

        setLoading(true);
        setHasError(false);

        const id = Number(safeLessonId);

        getAdminLessonDetailsResponse(id)
            .then((res) => {
                if (!cancelled) setLesson(res);
            })
            .catch((error: any) => {
                if (cancelled) return;
                setHasError(true);
                setLesson(null);
                showAlert(
                    error?.message ?? t("lessondetails.errors.loadFailed"),
                    "error"
                );
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeLessonId]);

    if (loading && !lesson) {
        return (
            <Container sx={{mt: 6, display: "flex", justifyContent: "center"}}>
                <CircularProgress/>
            </Container>
        );
    }

    if (hasError) {
        return (
            <Container sx={{mt: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2}}>
                <Typography color="text.secondary">
                    {t("lessondetails.errors.loadFailed")}
                </Typography>

                <Box sx={{display: "flex", gap: 2}}>
                    <Button variant="contained" onClick={() => load()}>
                        {t("common.retry")}
                    </Button>

                    <Button component={Link} to="/" variant="outlined">
                        {t("common.toMainPage")}
                    </Button>
                </Box>
            </Container>
        );
    }

    if (!lesson) return null;

    const isLecture = (lesson.lessonContentType ?? "LECTURE") === "LECTURE";

    return (
        <Box sx={{px: {xs: 3, md: 3}}}>
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small"/>}
                maxItems={3}
                itemsBeforeCollapse={1}
                itemsAfterCollapse={2}
                sx={{
                    mb: 2,
                    "& .MuiBreadcrumbs-ol": {
                        overflow: "hidden",
                    },
                    "& .MuiBreadcrumbs-li": {
                        minWidth: 0,
                    },
                }}
            >
                <BreadcrumbLink to="/">
                    {t("nav.courses")}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/course/${lesson.courseId}`}>
                    {lesson.courseTitle}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/chapter/${lesson.chapterId}`}>
                    {lesson.chapterTitle}
                </BreadcrumbLink>

                <Typography color="text.primary" variant="body2">
                    <strong>{lesson.title}</strong>
                </Typography>
            </Breadcrumbs>

            <Box
                sx={{
                    "& pre": {
                        p: 1.5,
                        borderRadius: 1,
                        overflow: "auto",
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.paper !important",
                    },
                    "& pre[style]": {
                        background: "transparent !important",
                    },
                    "& code": {
                        fontFamily: "monospace",
                        fontSize: 14,
                    },
                }}
                dangerouslySetInnerHTML={{__html: lesson.content}}
            />

            {isLecture && (
                <Box sx={{mt: 3}}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
                            gap: 2,
                        }}
                    >
                        <Paper
                            component={Link}
                            to={`/lesson/${lesson.id}/tasks`}
                            elevation={0}
                            sx={(theme) => ({
                                p: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                                textDecoration: "none",
                                color: "inherit",
                                display: "block",
                                transition: "background-color 0.15s ease",
                                "&:hover": {backgroundColor: theme.palette.action.hover},
                                "&:focus-visible": {
                                    outline: `2px solid ${theme.palette.primary.main}`,
                                    outlineOffset: 2,
                                },
                            })}
                        >
                            <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                <Box
                                    sx={(theme) => ({
                                        width: 40,
                                        height: 40,
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: theme.palette.action.hover,
                                        flexShrink: 0,
                                    })}
                                >
                                    <CodeIcon fontSize="small"/>
                                </Box>

                                <Box sx={{minWidth: 0, flexGrow: 1}}>
                                    <Typography sx={{fontWeight: 800}} noWrap>
                                        {t("lessondetails.codeTasks")}
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {t("lessondetails.codeTasksDescription")}
                                    </Typography>
                                </Box>

                                <ChevronRightIcon fontSize="small"/>
                            </Box>
                        </Paper>

                        <Paper
                            component={Link}
                            to={`/lesson/${lesson.id}/practices`}
                            elevation={0}
                            sx={(theme) => ({
                                p: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                                textDecoration: "none",
                                color: "inherit",
                                display: "block",
                                transition: "background-color 0.15s ease",
                                "&:hover": {backgroundColor: theme.palette.action.hover},
                                "&:focus-visible": {
                                    outline: `2px solid ${theme.palette.primary.main}`,
                                    outlineOffset: 2,
                                },
                            })}
                        >
                            <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                <Box
                                    sx={(theme) => ({
                                        width: 40,
                                        height: 40,
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: theme.palette.action.hover,
                                        flexShrink: 0,
                                    })}
                                >
                                    <AssignmentIcon fontSize="small"/>
                                </Box>

                                <Box sx={{minWidth: 0, flexGrow: 1}}>
                                    <Typography sx={{fontWeight: 800}} noWrap>
                                        {t("lessondetails.practiceTasks")}
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {t("lessondetails.practiceTasksDescription")}
                                    </Typography>
                                </Box>

                                <ChevronRightIcon fontSize="small"/>
                            </Box>
                        </Paper>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default LessonDetailsPage;